import { ArrowUp, Square } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { cn } from '../lib/utils'
import { Textarea } from './textarea'
import type { KeyboardEvent, ReactNode, RefObject } from 'react'

// ChatComposer is the shared chat input, styled as a single rounded "card": an auto-growing,
// borderless textarea above a slim action bar. Enter submits; Shift+Enter inserts a newline.
// While `isStreaming`, the textarea locks and the send affordance becomes a Stop button wired to
// `onStop`. Callers needing extra controls (switches, sliders) pass them via `accessory`, which
// renders just below the card, and own that state.
export function ChatComposer({
  onSend,
  isDisabled = false,
  isStreaming = false,
  onStop,
  placeholder = 'Reply...',
  accessory,
  header,
  inputRef
}: {
  onSend: (text: string) => void | Promise<void>
  isDisabled?: boolean
  isStreaming?: boolean
  onStop?: () => void
  placeholder?: string
  accessory?: ReactNode
  header?: ReactNode
  inputRef?: RefObject<HTMLTextAreaElement | null>
}) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const setTextareaRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      textareaRef.current = el
      if (inputRef) inputRef.current = el
    },
    [inputRef]
  )

  const inputLocked = isDisabled || isStreaming
  const canSend = !inputLocked && value.trim().length > 0
  const showStop = isStreaming && onStop !== undefined

  const autoResize = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const maxHeight = window.innerHeight * 0.4
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [])

  const submit = useCallback(() => {
    const trimmed = value.trim()
    if (trimmed.length === 0 || inputLocked) return
    void onSend(trimmed)
    setValue('')
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.overflowY = 'hidden'
    }
  }, [value, inputLocked, onSend])

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter is a newline. Skip while an IME composition is in flight so
    // confirming a candidate with Enter does not fire a send.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <form
        className={cn(
          'flex flex-col rounded-2xl border border-input bg-background shadow-sm',
          'transition-colors focus-within:border-muted-foreground/40',
          inputLocked && 'opacity-90'
        )}
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        {header}
        <Textarea
          ref={setTextareaRef}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            autoResize()
          }}
          onKeyDown={handleKeyDown}
          disabled={inputLocked}
          rows={1}
          className="min-h-0 resize-none border-0 bg-transparent px-4 pt-3 pb-1 text-sm shadow-none focus-visible:border-0 focus-visible:ring-0 disabled:opacity-100 disabled:placeholder:opacity-60 dark:bg-transparent"
        />
        <div className="flex items-center justify-end px-2.5 pb-2.5">
          {showStop ? (
            <button
              type="button"
              onClick={onStop}
              aria-label="Stop generating"
              className="inline-flex size-8 items-center justify-center rounded-full bg-foreground text-background transition-colors outline-none hover:bg-foreground/85 focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <Square className="size-3.5 fill-current" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!canSend}
              aria-label="Send message"
              className={cn(
                'inline-flex size-8 items-center justify-center rounded-full transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                canSend
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'cursor-default bg-muted text-muted-foreground/60'
              )}
            >
              <ArrowUp className="size-4" />
            </button>
          )}
        </div>
      </form>
      {accessory}
    </div>
  )
}
